import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Icons } from "../constants/icon-library";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import { useFormik } from "formik";
import { editUser, getLoggedInUser } from "../features/user/userSlice";
import LoadingComponent from "../components/common/LoadingComponent";
import {
  profileInitialValues,
  profileValidationSchema,
} from "../constants/auth.constants";
import TimezonePicker from "../components/common/TimezonePicker";

function Profile() {
  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => state.user);

  const [editMode, setEditMode] = useState(false);
  const [phoneCount, setPhoneCount] = useState(1);

  const phoneInputRefs = useRef({});
  const itiRefs = useRef({});
  const formikRef = useRef(null);
  const handlersRefs = useRef({});

  useEffect(() => {
    if (user?.uuid) {
      dispatch(getLoggedInUser(user.uuid));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extractDigits = (str) => (str ? str.replace(/\D/g, "") : "");

  const formik = useFormik({
    initialValues: profileInitialValues,
    validationSchema: profileValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      await handleSave(values);
    },
  });

  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  useEffect(() => {
    if (!user) return;

    const phoneArray = Array.isArray(user.phoneNumber)
      ? user.phoneNumber.filter((p) => p && p.trim() !== "")
      : user.phoneNumber && user.phoneNumber.trim() !== ""
        ? [user.phoneNumber]
        : [];

    const phones = phoneArray.length > 0 ? phoneArray : [""];

    setPhoneCount(phones.length);

    formik.setValues(
      {
        name: user.name || "",
        phoneNumber: phones,
        timezone: user.timezone || "UTC",
      },
      false,
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const initializePhoneInput = useCallback((index, phone) => {
    const inputEl = phoneInputRefs.current[index];
    if (!inputEl) return;

    if (itiRefs.current[index]) {
      try {
        itiRefs.current[index].destroy();
      } catch {}
    }

    if (handlersRefs.current[index]) {
      if (handlersRefs.current[index].input) {
        inputEl.removeEventListener("input", handlersRefs.current[index].input);
      }
      if (handlersRefs.current[index].countrychange) {
        inputEl.removeEventListener(
          "countrychange",
          handlersRefs.current[index].countrychange,
        );
      }
    }

    const iti = intlTelInput(inputEl, {
      initialCountry: "us",
      preferredCountries: ["in", "us", "gb"],
      separateDialCode: true,
      utilsScript:
        "https://cdn.jsdelivr.net/npm/intl-tel-input@23.6.0/build/js/utils.js",
    });

    itiRefs.current[index] = iti;

    if (phone) {
      try {
        iti.setNumber(phone);
      } catch {}
    }

    const handlePhone = () => {
      const raw = inputEl.value.trim();
      let num = "";

      if (/^[0-9\s()+-]+$/.test(raw)) {
        try {
          num = iti.getNumber();
        } catch {}

        if (!num || !num.startsWith("+")) {
          const c = iti.getSelectedCountryData();
          const d = extractDigits(raw);
          if (c?.dialCode && d) num = `+${c.dialCode}${d}`;
          else num = raw;
        }
      } else num = raw;

      const updatedPhones = [...formikRef.current.values.phoneNumber];
      updatedPhones[index] = num;
      formikRef.current.setFieldValue("phoneNumber", updatedPhones, true);
    };

    const handleCountry = () => {
      const c = iti.getSelectedCountryData();
      const digits = extractDigits(inputEl.value);
      if (c?.dialCode && digits) {
        const updatedPhones = [...formikRef.current.values.phoneNumber];
        updatedPhones[index] = `+${c.dialCode}${digits}`;
        formikRef.current.setFieldValue("phoneNumber", updatedPhones, true);
      }
    };

    handlersRefs.current[index] = {
      input: handlePhone,
      countrychange: handleCountry,
    };

    inputEl.addEventListener("input", handlePhone);
    inputEl.addEventListener("countrychange", handleCountry);
  }, []);

  useEffect(() => {
    if (!editMode) {
      Object.keys(itiRefs.current).forEach((key) => {
        const index = parseInt(key);
        if (itiRefs.current[index]) {
          try {
            itiRefs.current[index].destroy();
          } catch {}
          itiRefs.current[index] = null;
        }
      });
      phoneInputRefs.current = {};
      handlersRefs.current = {};
      return;
    }

    const timer = setTimeout(() => {
      const phones = formikRef.current?.values?.phoneNumber || [];
      phones.forEach((phone, index) => {
        if (phoneInputRefs.current[index]) {
          initializePhoneInput(index, phone);
        }
      });
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [editMode, phoneCount, initializePhoneInput]);

  const handleAddPhone = () => {
    const currentPhones = formik.values.phoneNumber;
    formik.setFieldValue("phoneNumber", [...currentPhones, ""], false);
    setPhoneCount((prev) => prev + 1);

    setTimeout(() => {
      const newIndex = currentPhones.length;
      if (phoneInputRefs.current[newIndex]) {
        initializePhoneInput(newIndex, "");
      }
    }, 200);
  };

  const handleRemovePhone = (index) => {
    const currentPhones = formik.values.phoneNumber;
    if (currentPhones.length <= 1) return;

    const updatedPhones = currentPhones.filter((_, i) => i !== index);
    formik.setFieldValue("phoneNumber", updatedPhones, false);
    setPhoneCount(updatedPhones.length);
  };

  const handleSave = useCallback(
    async (values) => {
      const allPhones = values.phoneNumber
        .map((phone, index) => {
          let fullPhone = phone || "";

          if (itiRefs.current[index] && phoneInputRefs.current[index]) {
            try {
              const num = itiRefs.current[index].getNumber();
              if (num?.startsWith("+")) fullPhone = num;
            } catch {}
          }

          return fullPhone;
        })
        .filter((p) => p?.trim());

      const body = {
        name: values.name,
        phoneNumber: allPhones,
        timezone:
          typeof values.timezone === "object"
            ? values.timezone.value
            : values.timezone,
      };

      const action = await dispatch(editUser({ uuid: user.uuid, data: body }));

      if (editUser.fulfilled.match(action)) {
        if (editUser.fulfilled.match(action)) {
          toast.success("Profile updated successfully!");
          setEditMode(false);
        }
      } else {
        toast.error(
          action.payload?.message ||
            action.error?.message ||
            "Failed to update profile",
        );
      }
    },
    [dispatch, user.uuid],
  );

  const handleCancel = () => {
    if (!user) return;

    const phoneArray = Array.isArray(user.phoneNumber)
      ? user.phoneNumber.filter((p) => p && p.trim() !== "")
      : user.phoneNumber && user.phoneNumber.trim() !== ""
        ? [user.phoneNumber]
        : [];

    const phones = phoneArray.length > 0 ? phoneArray : [""];
    setPhoneCount(phones.length);

    formik.setValues(
      {
        name: user.name,
        phoneNumber: phones,
        timezone: user.timezone || "UTC",
      },
      false,
    );

    formik.setTouched({}, false);
    formik.setErrors({});
    setEditMode(false);
  };

  return (
    <div className="container py-12">
      <div className="card border-0 shadow-lg">
        <div className="card-body p-5 rounded-4">
          {loading || !user ? (
            <LoadingComponent loading size={15} />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="position-relative">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        width: 64,
                        height: 64,
                        background: "#7bdff2",
                        color: "#022c22",
                        fontSize: "1.5rem",
                      }}
                    >
                      {user.name?.charAt(0)?.toUpperCase()}
                    </div>

                    <span
                      className="position-absolute rounded-circle"
                      style={{
                        width: 10,
                        height: 10,
                        background: "#22c55e",
                        bottom: 4,
                        right: 4,
                      }}
                    />
                  </div>

                  <div>
                    <h3 className="mb-1 fw-semibold">{formik.values.name}</h3>
                    <small className="text-muted">User Profile</small>
                  </div>
                </div>

                {!editMode && (
                  <button
                    className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                    onClick={() => setEditMode(true)}
                  >
                    <i className={`wb ${Icons("edit")}`} />
                    Edit Profile
                  </button>
                )}
              </div>

              <hr className="border-secondary opacity-25 mb-4" />

              <form onSubmit={formik.handleSubmit}>
                <div className="mb-3">
                  <small className="text-muted fw-semibold d-flex align-items-center gap-2 mb-2">
                    <i className={`wb ${Icons("user")}`} />
                    Full Name
                  </small>

                  {editMode ? (
                    <input
                      type="text"
                      name="name"
                      className="form-control rounded-3 py-3"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                    />
                  ) : (
                    <div className="px-3 py-3 fw-medium">{user.name}</div>
                  )}
                </div>

                <div className="mb-3">
                  <small className="text-muted fw-semibold d-flex align-items-center gap-2 mb-2">
                    <i className={`wb ${Icons("email")}`} />
                    Email Address
                  </small>

                  <div className="px-3 py-3">{user.email}</div>
                </div>

                <div className="mb-3">
                  <small className="text-muted fw-semibold d-flex align-items-center gap-2 mb-2">
                    <i className={`wb ${Icons("calendar")}`} />
                    Timezone
                  </small>

                  {editMode ? (
                    <TimezonePicker
                      value={formik.values.timezone}
                      onChange={(timezone) =>
                        formik.setFieldValue("timezone", timezone)
                      }
                    />
                  ) : (
                    <div className="px-3 py-3">{user.timezone || "UTC"}</div>
                  )}
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted fw-semibold d-flex align-items-center gap-2">
                      <i className={`wb ${Icons("phone")}`} />
                      Phone Numbers
                    </small>

                    {editMode && (
                      <button
                        type="button"
                        className="btn btn-link text-info text-decoration-none p-0 fw-semibold"
                        onClick={handleAddPhone}
                      >
                        + Add Phone
                      </button>
                    )}
                  </div>

                  {editMode ? (
                    <div className="d-flex flex-column gap-3">
                      {formik.values.phoneNumber.map((phone, index) => (
                        <div
                          key={`phone-${index}`}
                          className="d-flex align-items-center gap-3"
                        >
                          <span
                            className="badge bg-info d-flex align-items-center justify-content-center"
                            style={{
                              minWidth: "48px",
                              height: "32px",
                            }}
                          >
                            #{index + 1}
                          </span>

                          <div className="flex-grow-1">
                            <input
                              ref={(el) => {
                                if (el) phoneInputRefs.current[index] = el;
                              }}
                              type="tel"
                              className="form-control"
                              defaultValue={phone}
                            />
                          </div>

                          {formik.values.phoneNumber.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemovePhone(index)}
                            >
                              <i className={`wb ${Icons("delete")}`} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {user.phoneNumber?.length ? (
                        user.phoneNumber.filter(Boolean).map((phone, index) => (
                          <div
                            key={index}
                            className="d-flex align-items-center gap-3 px-3 py-2"
                          >
                            <span
                              className="badge bg-info d-flex align-items-center justify-content-center"
                              style={{
                                minWidth: "48px",
                                height: "30px",
                              }}
                            >
                              #{index + 1}
                            </span>

                            <span>{phone}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-muted px-3">Not provided</span>
                      )}
                    </div>
                  )}
                </div>

                {editMode && (
                  <div className="d-flex justify-content-between align-items-center mt-5">
                    <button
                      type="button"
                      className="btn btn-outline-danger px-4"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary px-4"
                      disabled={!formik.isValid}
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
